import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@arcadeai/design-system/components/ui/atoms/dropdown-menu";
import type { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { Settings2 } from "lucide-react";
import { useMemo } from "react";
import {
  getColumnDisplayNames,
  useColumnVisibility,
} from "./use-column-visibility";

export type TableViewOptionsProps<TData = unknown> = {
  columns: ColumnDef<TData>[];
  storageKey: string;
  onVisibilityChange?: (
    visibility: VisibilityState,
    setter: (
      value: VisibilityState | ((val: VisibilityState) => VisibilityState)
    ) => void
  ) => void;
  label?: string;
  className?: string;
  hideColumns?: string[];
};

export type TableViewOptionsReturn = {
  columnVisibility: VisibilityState;
  setColumnVisibility: (
    value: VisibilityState | ((val: VisibilityState) => VisibilityState)
  ) => void;
  component: React.ReactElement;
};

/**
 * Generic hook for table view options with column visibility management
 * Works with any table data type and provides localStorage persistence
 */
export function useTableViewOptions<TData = unknown>({
  columns,
  storageKey,
  onVisibilityChange,
  label = "Columns",
  className = "h-8 px-4 gap-2 shadow-sm",
  hideColumns = [],
}: TableViewOptionsProps<TData>): TableViewOptionsReturn {
  // Get column visibility state from localStorage
  const [columnVisibility, setColumnVisibility] = useColumnVisibility(
    columns,
    storageKey
  );

  // Get display names for the dropdown
  const columnDisplayNames = useMemo(
    () => getColumnDisplayNames(columns),
    [columns]
  );

  // Get hideable columns (exclude actions and non-hideable)
  const hideableColumns = useMemo(
    () =>
      columns
        .filter(
          (col) =>
            col.id &&
            col.id !== "actions" &&
            col.enableHiding !== false &&
            !hideColumns.includes(col.id)
        )
        .map((col) => ({
          // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
          id: col.id as string,
          // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
          displayName: columnDisplayNames[col.id as string],
        })),
    [columns, columnDisplayNames, hideColumns]
  );

  // Notify parent of visibility changes
  const handleVisibilityChange = (
    updater: VisibilityState | ((val: VisibilityState) => VisibilityState)
  ) => {
    setColumnVisibility(updater);
    onVisibilityChange?.(columnVisibility, setColumnVisibility);
  };

  const component = (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={<Button className={className} size="sm" variant="outline" />}
      >
        <Settings2 className="h-4 w-4" />
        <span className="hidden sm:inline">{label}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[180px]">
        <DropdownMenuLabel className="text-xs">Columns</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {hideableColumns.map((column) => (
          <DropdownMenuCheckboxItem
            checked={columnVisibility[column.id] ?? true}
            key={column.id}
            onCheckedChange={(checked) =>
              handleVisibilityChange((prev) => ({
                ...prev,
                [column.id]: checked ?? false,
              }))
            }
          >
            {column.displayName}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return {
    columnVisibility,
    setColumnVisibility: handleVisibilityChange,
    component,
  };
}
